/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { LogLevels } from '@tivocorp/tivo-webapp-loggers';
import { Feature } from 'client-code/fe-fav-service';

import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { Storage } from '../storage/Storage';

export class DeviceFeatures {
    public static APP_ACT_CLIENT_SOCU = 'AppActClientSocu';

    public static FORCE_BACKHAUL = 'forceBackhaul';

    public static MSO_SERVICE_ID = 'msoServiceId';

    // TODO: Remove custom flags once available from deviceFeatureSearch
    // Flags to enable/ disable filtering of PPV, jump and 4K channels
    private static FILTER_JUMP_CHANNELS: boolean = true;

    private static FILTER_PPV_CHANNELS: boolean = true;

    private static FILTER_4K_CHANNELS: boolean = true;

    private static IS_SOCU_ENABLED: boolean = false;

    private static mBackhaul: Feature;

    private static mLoggingWindow: any = Storage.getInstance().getItem(Storage.Keys.LOGGING_WINDOW);

    private static mMsoServiceId: string = Storage.getInstance().getItem(
        Storage.Keys.MSO_SERVICE_ID,
    );

    private static mIsMsoServiceIdChanged: boolean;

    private static mIsMsoServiceIdValid: boolean = false;

    public static getDefaultForceBackhaul() {
        return {
            featureName: DeviceFeatures.FORCE_BACKHAUL,
            attributes: [
                {
                    attributeName: 'enable',
                    attributeValue: 'false',
                },
                {
                    attributeName: 'logVerbosity',
                    // __START_DEBUG_CODE_CLEANUP__
                    attributeValue: 'INFO',
                    // __END_DEBUG_CODE_CLEANUP__
                    // __UNCOMMENT_DEBUG_CODE__
                    // attributeValue: 'ERROR',
                },
            ],
        };
    }

    public static getDefaultMsoServiceId() {
        return {
            featureName: DeviceFeatures.MSO_SERVICE_ID,
            attributes: [
                {
                    attributeName: 'msoServiceIdName',
                    attributeValue: '',
                },
            ],
        };
    }

    public static initBackhaulFeature(aBackhaul: Feature) {
        DeviceFeatures.mBackhaul = aBackhaul;
        const sLoggingWindow = aBackhaul?.attributes?.find((attribute) => {
            return attribute.attributeName === 'loggingWindow';
        })?.attributeValue;
        const oLoggingWindow = sLoggingWindow && JSON.parse(sLoggingWindow);
        const tmpLoggingWindow = {};
        if (sLoggingWindow) {
            Object.keys(oLoggingWindow).forEach((key) => {
                const timeStrings = oLoggingWindow[key]?.split('-');
                tmpLoggingWindow[key] = {
                    startTime: Number(timeStrings[0] || 0) * MILLISECONDS_IN_A_SECOND,
                    endTime: Number(timeStrings[1] || 0) * MILLISECONDS_IN_A_SECOND,
                };
            });
        }
        DeviceFeatures.mLoggingWindow = tmpLoggingWindow;
        Storage.getInstance().setItem(Storage.Keys.LOGGING_WINDOW, tmpLoggingWindow);
    }

    public static initMsoServiceIdFeature(aMsoServiceId: Feature) {
        const msoServiceId = aMsoServiceId?.attributes?.find((attribute) => {
            return attribute.attributeName === 'msoServiceIdName';
        })?.attributeValue;

        DeviceFeatures.mIsMsoServiceIdValid = !!msoServiceId;

        if (DeviceFeatures.mIsMsoServiceIdValid) {
            DeviceFeatures.mIsMsoServiceIdChanged = !(
                DeviceFeatures.mMsoServiceId && msoServiceId === DeviceFeatures.mMsoServiceId
            );
            DeviceFeatures.mMsoServiceId = msoServiceId!;
            Storage.getInstance().setItem(Storage.Keys.MSO_SERVICE_ID, msoServiceId);
        }
    }

    public static initSocuFeature(socuFeature: Feature) {
        const appActClientSocuEnabled: boolean = !!socuFeature?.attributes?.find(
            (attribyte) => attribyte.attributeName === 'enabled',
        )?.attributeValue;
        DeviceFeatures.IS_SOCU_ENABLED = appActClientSocuEnabled;
    }

    public static isMsoServiceIdChanged(): boolean {
        return DeviceFeatures.mIsMsoServiceIdChanged;
    }

    public static isMsoServiceIdValid(): boolean {
        return DeviceFeatures.mIsMsoServiceIdValid;
    }

    public static isSocuEnabled(): boolean {
        return DeviceFeatures.IS_SOCU_ENABLED;
    }

    public static getMsoServiceId() {
        return DeviceFeatures.mMsoServiceId;
    }

    public static isBackhaulEnabled(): boolean {
        const status = DeviceFeatures.mBackhaul?.attributes?.find((attribute) => {
            return attribute.attributeName === 'enable';
        })?.attributeValue;
        return status === 'true';
    }

    public static getBackhaulVerbosity(): LogLevels {
        const logLevel = DeviceFeatures.mBackhaul?.attributes?.find((attribute) => {
            return attribute.attributeName === 'logVerbosity';
        })?.attributeValue;
        // If the logVerbosity is not received from deviceFeature, set it to default value ERROR
        return logLevel ? LogLevels[logLevel] : LogLevels.ERROR;
    }

    public static isDiagnosticLoggingEnabled(): boolean {
        const diagnosticLogging = DeviceFeatures.mLoggingWindow?.diagnosticLogging;
        return diagnosticLogging
            ? Date.now() >= diagnosticLogging.startTime && Date.now() <= diagnosticLogging.endTime
            : false;
    }

    public static isServiceApiLoggingEnabled(): boolean {
        const serviceApiLogging = DeviceFeatures.mLoggingWindow?.serviceApiLogging;
        return serviceApiLogging
            ? Date.now() >= serviceApiLogging.startTime && Date.now() <= serviceApiLogging.endTime
            : false;
    }

    public static isWhinyLoggingEnabled(): boolean {
        const whinyLogging = DeviceFeatures.mLoggingWindow?.whinyLogging;
        return whinyLogging
            ? Date.now() >= whinyLogging.startTime && Date.now() <= whinyLogging.endTime
            : false;
    }

    public static shouldFilterJumpChannels(): boolean {
        return this.FILTER_JUMP_CHANNELS;
    }

    public static shouldFilterPpvChannels(): boolean {
        return this.FILTER_PPV_CHANNELS;
    }

    public static shouldFilter4KChannels(): boolean {
        return this.FILTER_4K_CHANNELS;
    }
}
